import { useCallback, useEffect, useRef } from "react";
import { Box, AlertIcon, Alert, VStack, Text } from "@chakra-ui/react";
import { useHistory, useLocation } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { FullPageSpinner } from "components/Spinner";
import { useGetTransactionMethods } from "api/transaction-methods";
import { PaymentMethodList } from "components/PaymentMethodSelector";
import BankAccount from "./BankAccount";
import { useGenerateUrlAndRedirectUser } from "containers/Pages/PlaidACH/useGenerateUrlAndRedirectUser";
import AddBankAccountButton from "components/AddPaymentMethod/AddBankAccountButton";
import { useSessionStorage, useAnalytics } from "utils";
import { useSettings } from "utils/settingsContext";
import { useFormikContext } from "formik";
import { AddPaypalButton } from "components/Paypal";

const PayoutDestination = ({ path, onPayoutDestinationSelected, onStoreFormValuesTemporarily }) => {
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);
  const location = useLocation();
  const history = useHistory();
  const { appSettings } = useSettings();
  const { values, setValues } = useFormikContext();
  const { onGenerateUrlAndRedirectUser } = useGenerateUrlAndRedirectUser();
  const [, setUserDefinedPayoutInfo] = useSessionStorage("tilia.ac.userDefinedPayoutInfo");
  const {
    data: transactionMethodsData,
    isPending: isLoading,
    isError,
    refetch,
  } = useGetTransactionMethods({
    payout_options: true,
    bank_accounts: true,
  });

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("PayoutV2", "PayoutDestination_Viewed");
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired]);

  /**
   * Handles bank country selection.
   * @param {Object} countryOption - The selected country option.
   */
  const handleAddBankByCountryOption = (countryOption) => {
    const type = countryOption.type;
    if (type === "plaid") {
      if (values.amount) {
        const valsToStore = JSON.stringify({
          amount: values.amount,
          sourcePaymentMethodId: values.sourcePaymentMethodId,
        });
        setUserDefinedPayoutInfo(valsToStore);
      }
      onGenerateUrlAndRedirectUser();
    } else if (type === "masspay-form") {
      onStoreFormValuesTemporarily(values);
      history.push({
        pathname: `${path}/bank-account-details`,
        state: {
          countryISO: countryOption.ISO,
          currency: countryOption.currency,
        },
      });
    }
  };

  const getBankPayoutRailsVendorByCountry = useCallback((countryCode) => {
    const countryLookup = appSettings?.supported_bank_account_countries || {};
    const countrySettings = countryLookup[countryCode] || {};
    return countrySettings.payout_rails_vendor; // downstream code will default to "masspay" if this is undefined
  }, [appSettings]);

  const handleDestinationSelection = useCallback(
    (payoutDest) => {
      if ("bank_account_id" in payoutDest) {
        setValues((prevValues) => {
          return {
            ...prevValues,
            payoutDestination: payoutDest,
            bankRailsVendor: getBankPayoutRailsVendorByCountry(payoutDest.country_iso) || "masspay",
          };
        });
      } else {
        // for payout methods like PayPal
        setValues((prevValues) => {
          return {
            ...prevValues,
            payoutDestination: payoutDest,
            destinationPaymentMethodId: payoutDest.id,
          };
        });
      }
      onPayoutDestinationSelected(payoutDest);
    },
    [onPayoutDestinationSelected, setValues, getBankPayoutRailsVendorByCountry],
  );

  const handleNewBankOrPayPalAndRedirect = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);
    const newlyAddedBankAccountId = queryParams.get("newlyAddedBankAccountId");
    const newlyAddedPayPalId = queryParams.get("newlyAddedPayPalId");
    if (!newlyAddedBankAccountId && !newlyAddedPayPalId) return;

    /* check for new bank account */
    const bankAccount = Object.values(transactionMethodsData?.payload?.bank_accounts || {}).find(
      (v) => v.bank_account_id === newlyAddedBankAccountId,
    );
    const isNewBankAccount = newlyAddedBankAccountId && bankAccount;

    /* check for new paypal */
    const payPalPM = Object.values(transactionMethodsData?.payload?.payment_methods || {}).find(
      (pm) => pm.id === newlyAddedPayPalId,
    );
    const isNewPayPal = newlyAddedPayPalId && payPalPM;

    if (isNewBankAccount || isNewPayPal) {
      queryParams.delete("newlyAddedBankAccountId");
      queryParams.delete("newlyAddedPayPalId");
      history.replace({
        search: queryParams.toString(),
      });
      let newPM = bankAccount || payPalPM; // there can be only 1 at this point
      handleDestinationSelection(newPM);
    }
  }, [location, history, transactionMethodsData, handleDestinationSelection]);

  /**
   * useEffect to handle a new bank account having been added
   * and us immediately taking the user to the Masspay options view.
   */
  useEffect(() => {
    handleNewBankOrPayPalAndRedirect();
  }, [handleNewBankOrPayPalAndRedirect]);

  if (isLoading) {
    return <FullPageSpinner data-testid="payout-method-selector" />;
  }

  if (isError) {
    return (
      <>
        <Alert status="error">
          <AlertIcon />
          <FormattedMessage
            defaultMessage="There was an error retrieving transaction method details.  Please try again later."
            description="Section header"
          />
        </Alert>
      </>
    );
  }

  const allowedPayoutIDs = transactionMethodsData?.payload?.capabilities?.payout || [];
  const filteredPayoutMethods = Object.values(
    transactionMethodsData?.payload?.payment_methods || {},
  ).filter((pm) => {
    return allowedPayoutIDs.includes(pm.payment_method_id) && pm.method_class !== "banking" && pm.provider !== "wallet"; // removing 'wallet' as an option as it looks like it can be included in 'capabilities?.payout' and in this case we're only paying outside of our system
  });
  // mark default pm
  const processedPayoutPMs = filteredPayoutMethods.map((pm) => {
    const isDefault = pm.tags.some(
      (tag) => tag.tag === "default_payout" && tag.status === "ACTIVE",
    );
    return { ...pm, isDefault }; // need to spread original object so isDefault doesn't get overwriten in other context
  });
  const bankAccounts = Object.values(transactionMethodsData?.payload?.bank_accounts || {});

  return (
    <Box py="6">
      <Box pb="2">
        <Text fontSize="lg" as="b">
          <FormattedMessage
            defaultMessage="Please select a payout destination."
            description="Section header"
          />
        </Text>
      </Box>
      <PaymentMethodList
        paymentMethods={processedPayoutPMs}
        isPayout={true}
        onUpdate={refetch}
        selectedPaymentMethodId={null}
        onSelect={handleDestinationSelection}
        onPayoutOptionSelect={() => {}}
      />
      <Box pb="6">
        <AddPaypalButton
          useTextUI={true}
          prepareTokenOnLoad={false}
          onPreClickCallback={() => {
            if (values.amount) {
              const valsToStore = JSON.stringify({
                amount: values.amount,
                sourcePaymentMethodId: values.sourcePaymentMethodId,
              });
              setUserDefinedPayoutInfo(valsToStore);
            }
          }}
        />
      </Box>
      {bankAccounts.length > 0 && (
        <VStack pb="6">
          {bankAccounts.map((bankAccount) => {
            return (
              <BankAccount
                width="100%"
                key={`bank-account-${bankAccount.bank_account_id}`}
                bankAccount={bankAccount}
                onBankAccountClick={handleDestinationSelection}
                onUpdate={refetch}
                transition="background-color 0.2s ease-out 10ms"
                _hover={{
                  bgColor: "lightBackground",
                  borderColor: "integrator.300",
                }}
              />
            );
          })}
        </VStack>
      )}
      <Box pb="6">
        <AddBankAccountButton onSelect={handleAddBankByCountryOption} countrySelectSize="xs" />
      </Box>
    </Box>
  );
};

export default PayoutDestination;
